import { create } from "zustand";
import type {
  BootstrapDto,
  CandleDto,
  CandleInterval,
  GatewayStatusDto,
  InstrumentDto,
  OperationDto,
  OrderBookDto,
  OrderDto,
  PortfolioDto,
  QuoteDto,
  ServerWsMessage,
} from "../shared/contracts.ts";
import { dedupeInstruments, parseWatchlist } from "./watchlist.ts";

const DEFAULT_STATUS: GatewayStatusDto = {
  state: "stale",
  streams: {},
  updatedAt: new Date(0).toISOString(),
};

interface TerminalState {
  bootstrap?: BootstrapDto;
  candles: CandleDto[];
  error?: string;
  interval: CandleInterval;
  loading: boolean;
  operations: OperationDto[];
  orderBook?: OrderBookDto;
  orders: OrderDto[];
  portfolio?: PortfolioDto;
  quotes: Record<string, QuoteDto>;
  selectedInstrument?: InstrumentDto;
  status: GatewayStatusDto;
  switchingAccount: boolean;
  watchlist: InstrumentDto[];
  addWatchlistInstrument: (instrument: InstrumentDto) => void;
  applyBootstrap: (bootstrap: BootstrapDto) => void;
  applyRealtime: (message: ServerWsMessage) => void;
  loadBootstrap: () => Promise<void>;
  select: (instrumentUid: string, interval?: CandleInterval) => Promise<void>;
  selectAccount: (accountKey: string) => Promise<void>;
  removeWatchlistInstrument: (instrumentUid: string) => void;
  setError: (error?: string) => void;
  setStatus: (status: GatewayStatusDto) => void;
}

const persistedInterval = localStorage.getItem("terminal.interval");
const initialInterval: CandleInterval = [
  "1m", "5m", "10m", "1h", "4h", "1d", "1w", "1mo",
].includes(persistedInterval ?? "")
  ? persistedInterval as CandleInterval
  : "1d";

let realtimeFrame: number | undefined;
let pendingRealtime: ServerWsMessage[] = [];
let selectionRequestId = 0;
let accountSwitchRequestId = 0;

function accountStorageKey(accountKey: string, value: "instrument" | "watchlist"): string {
  return `terminal.account.${accountKey}.${value}`;
}

function selectedInstrumentFor(accountKey: string): string | null {
  return localStorage.getItem(accountStorageKey(accountKey, "instrument"));
}

function persistWatchlist(accountKey: string, instruments: InstrumentDto[]): void {
  localStorage.setItem(accountStorageKey(accountKey, "watchlist"), JSON.stringify(instruments));
}

function watchlistForBootstrap(bootstrap: BootstrapDto): InstrumentDto[] {
  const key = accountStorageKey(bootstrap.account.key, "watchlist");
  const scoped = parseWatchlist(localStorage.getItem(key));
  const legacy = bootstrap.account.broker === "tinvest"
    ? parseWatchlist(localStorage.getItem("terminal.watchlist"))
    : undefined;
  const watchlist = scoped ?? legacy ?? dedupeInstruments(
    bootstrap.favorites.length > 0
      ? bootstrap.favorites
      : bootstrap.portfolio.positions.map((position) => position.instrument),
  );
  if (scoped === undefined) persistWatchlist(bootstrap.account.key, watchlist);
  return watchlist;
}

function bootstrapUrl(instrumentUid: string | null, interval: CandleInterval): string {
  const params = new URLSearchParams({ interval });
  if (instrumentUid) params.set("instrumentUid", instrumentUid);
  return `/api/bootstrap?${params}`;
}

function statusFromMessage(message: Extract<ServerWsMessage, { type: "connection.status" }>): GatewayStatusDto {
  const { type: _type, ...status } = message;
  return status;
}

async function accountBootstrapRequest(
  accountKey: string,
  interval: CandleInterval,
  instrumentUid?: string | null,
): Promise<Response> {
  return fetch("/api/accounts/select", {
    body: JSON.stringify({
      accountKey,
      ...(instrumentUid ? { instrumentUid } : {}),
      interval,
    }),
    headers: { "Content-Type": "application/json" },
    method: "POST",
  });
}

export const useTerminalStore = create<TerminalState>((set, get) => ({
  candles: [],
  interval: initialInterval,
  loading: true,
  operations: [],
  orders: [],
  quotes: {},
  status: DEFAULT_STATUS,
  switchingAccount: false,
  watchlist: [],

  addWatchlistInstrument(instrument) {
    set((state) => {
      if (state.watchlist.some((item) => item.uid === instrument.uid)) return state;
      const watchlist = dedupeInstruments([...state.watchlist, instrument]).slice(0, 200);
      if (state.bootstrap) persistWatchlist(state.bootstrap.account.key, watchlist);
      return { watchlist };
    });
  },

  applyBootstrap(bootstrap) {
    resetRealtimeQueue();
    const watchlist = watchlistForBootstrap(bootstrap);
    localStorage.setItem("terminal.account.active", bootstrap.account.key);
    if (bootstrap.selectedInstrument) {
      localStorage.setItem(
        accountStorageKey(bootstrap.account.key, "instrument"),
        bootstrap.selectedInstrument.uid,
      );
    }
    set({
      bootstrap,
      candles: bootstrap.candles,
      error: undefined,
      interval: bootstrap.interval,
      loading: false,
      operations: bootstrap.operations,
      orderBook: bootstrap.orderBook,
      orders: bootstrap.orders,
      portfolio: bootstrap.portfolio,
      quotes: bootstrap.quotes,
      selectedInstrument: bootstrap.selectedInstrument,
      status: bootstrap.status,
      switchingAccount: false,
      watchlist,
    });
  },

  applyRealtime(message) {
    pendingRealtime.push(message);
    if (realtimeFrame !== undefined) return;
    realtimeFrame = requestAnimationFrame(() => {
      const messages = pendingRealtime;
      pendingRealtime = [];
      realtimeFrame = undefined;
      set((state) => {
        let candles = state.candles;
        let operations = state.operations;
        let orderBook = state.orderBook;
        let orders = state.orders;
        let portfolio = state.portfolio;
        let quotes = state.quotes;
        let selectedInstrument = state.selectedInstrument;
        let status = state.status;

        for (const item of messages) {
          switch (item.type) {
            case "market.quote":
              quotes = { ...quotes, [item.quote.instrumentUid]: item.quote };
              break;
            case "market.orderbook":
              if (item.orderBook.instrumentUid === selectedInstrument?.uid) {
                orderBook = item.orderBook;
              }
              break;
            case "market.candle":
              if (
                item.candle.instrumentUid === selectedInstrument?.uid &&
                item.candle.interval === state.interval
              ) {
                const index = candles.findIndex((candle) => candle.time === item.candle.time);
                candles = index < 0
                  ? [...candles, item.candle].slice(-700)
                  : candles.map((candle, candleIndex) => candleIndex === index ? item.candle : candle);
              }
              break;
            case "portfolio.snapshot":
              portfolio = item.portfolio;
              break;
            case "orders.snapshot":
              orders = item.orders;
              break;
            case "operations.snapshot":
              operations = item.operations;
              break;
            case "connection.status":
              status = statusFromMessage(item);
              break;
            case "selection.changed":
            case "error":
              break;
          }
        }
        return { candles, operations, orderBook, orders, portfolio, quotes, selectedInstrument, status };
      });
    });
  },

  async loadBootstrap() {
    const activeAccountKey = localStorage.getItem("terminal.account.active");
    const savedInstrument = activeAccountKey
      ? selectedInstrumentFor(activeAccountKey)
      : localStorage.getItem("terminal.instrument");
    set({ loading: true, error: undefined });
    try {
      let response = activeAccountKey
        ? await accountBootstrapRequest(activeAccountKey, get().interval, savedInstrument)
        : await fetch(bootstrapUrl(savedInstrument, get().interval));
      if (!response.ok && savedInstrument && response.status !== 401) {
        if (activeAccountKey) {
          localStorage.removeItem(accountStorageKey(activeAccountKey, "instrument"));
          response = await accountBootstrapRequest(activeAccountKey, get().interval);
        } else {
          localStorage.removeItem("terminal.instrument");
          response = await fetch(bootstrapUrl(null, get().interval));
        }
      }
      if (!response.ok && activeAccountKey && response.status === 400) {
        localStorage.removeItem("terminal.account.active");
        response = await fetch(bootstrapUrl(null, get().interval));
      }
      if (response.status === 401) throw new Error("unauthorized");
      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as { message?: string };
        throw new Error(body.message || "Не удалось загрузить данные брокерского счёта");
      }
      get().applyBootstrap(await response.json() as BootstrapDto);
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Не удалось загрузить терминал",
        loading: false,
      });
    }
  },

  async select(instrumentUid, requestedInterval) {
    const requestId = ++selectionRequestId;
    const interval = requestedInterval ?? get().interval;
    const previousUid = get().selectedInstrument?.uid;
    const changed = previousUid !== instrumentUid || get().interval !== interval;
    const accountKey = get().bootstrap?.account.key;
    if (accountKey) {
      localStorage.setItem(accountStorageKey(accountKey, "instrument"), instrumentUid);
    }
    localStorage.setItem("terminal.interval", interval);
    set({
      candles: changed ? [] : get().candles,
      interval,
      orderBook: previousUid === instrumentUid ? get().orderBook : undefined,
    });

    try {
      const [instrumentResponse, candlesResponse, orderBookResponse] = await Promise.all([
        previousUid === instrumentUid
          ? Promise.resolve(undefined)
          : fetch(`/api/instruments/${encodeURIComponent(instrumentUid)}`),
        fetch(`/api/instruments/${encodeURIComponent(instrumentUid)}/candles?interval=${interval}`),
        previousUid === instrumentUid && get().orderBook
          ? Promise.resolve(undefined)
          : fetch(`/api/instruments/${encodeURIComponent(instrumentUid)}/orderbook`),
      ]);
      if (requestId !== selectionRequestId) return;
      if (instrumentResponse && !instrumentResponse.ok) throw new Error("Инструмент недоступен");
      if (!candlesResponse.ok) throw new Error("Свечи недоступны");
      const instrumentPayload = instrumentResponse
        ? await instrumentResponse.json() as { instrument: InstrumentDto }
        : undefined;
      const candlePayload = await candlesResponse.json() as { candles: CandleDto[] };
      const orderBookPayload = orderBookResponse?.ok
        ? await orderBookResponse.json() as { orderBook?: OrderBookDto }
        : undefined;
      if (requestId !== selectionRequestId) return;
      set({
        candles: candlePayload.candles,
        error: undefined,
        orderBook: orderBookPayload?.orderBook ?? get().orderBook,
        selectedInstrument: instrumentPayload?.instrument ?? get().selectedInstrument,
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : "Не удалось сменить инструмент" });
    }
  },

  async selectAccount(accountKey) {
    const current = get().bootstrap;
    if (!current || current.account.key === accountKey || get().switchingAccount) return;
    const requestId = ++accountSwitchRequestId;
    selectionRequestId += 1;
    resetRealtimeQueue();
    set({ error: undefined, switchingAccount: true });
    const savedInstrument = selectedInstrumentFor(accountKey);
    try {
      let response = await accountBootstrapRequest(accountKey, get().interval, savedInstrument);
      if (!response.ok && savedInstrument && response.status !== 401) {
        localStorage.removeItem(accountStorageKey(accountKey, "instrument"));
        response = await accountBootstrapRequest(accountKey, get().interval);
      }
      if (response.status === 401) throw new Error("unauthorized");
      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as { message?: string };
        throw new Error(body.message || "Не удалось переключить брокерский счёт");
      }
      const bootstrap = await response.json() as BootstrapDto;
      if (requestId !== accountSwitchRequestId) return;
      resetRealtimeQueue();
      get().applyBootstrap(bootstrap);
    } catch (error) {
      if (requestId !== accountSwitchRequestId) return;
      try {
        const rollback = await accountBootstrapRequest(
          current.account.key,
          current.interval,
          current.selectedInstrument?.uid,
        );
        if (rollback.ok) get().applyBootstrap(await rollback.json() as BootstrapDto);
      } catch {
        // The previous snapshot remains visible if both upstreams are unavailable.
      }
      set({
        error: error instanceof Error ? error.message : "Не удалось переключить счёт",
        switchingAccount: false,
      });
    }
  },

  removeWatchlistInstrument(instrumentUid) {
    set((state) => {
      const watchlist = state.watchlist.filter((instrument) => instrument.uid !== instrumentUid);
      if (state.bootstrap) persistWatchlist(state.bootstrap.account.key, watchlist);
      return { watchlist };
    });
  },

  setError(error) {
    set({ error });
  },

  setStatus(status) {
    set({ status });
  },
}));

export function resetRealtimeQueue(): void {
  pendingRealtime = [];
  if (realtimeFrame !== undefined) cancelAnimationFrame(realtimeFrame);
  realtimeFrame = undefined;
}
